import { useTranslations } from '@vocab/react';
import { Badge, Box, Checkbox, FieldMessage, Stack } from 'braid-design-system';
import type React from 'react';

import translations from '@seek/rr-caja/src/.vocab';

import { type AvailableAnswer, SelectionTypeView } from '../../sdk';
import AnswerList, { type AnswerChangeHandler } from '../AnswerList/AnswerList';
import type { StatefulRecommendedQuestion } from '../App/state';
import CertsyTooltip from '../CertsyTooltip/CertsyTooltip';
import { useExperiment } from '../Experiment/Experiment';
import { IconSeekPass } from '../Icons';

import { EssentialToggle } from './EssentialToggle';
import { SubHeader } from './SubHeader/SubHeader';

export type ToggleEssentialQuestionHandler = (questionId: string) => void;
export type ToggleQuestionHandler = (questionId: string) => void;
export type ToggleSelectionTypeViewHandler = (questionId: string) => void;
export type { AnswerChangeHandler as ToggleAnswerHandler };

interface Props {
  id: string;
  displayValidation?: boolean;
  question: StatefulRecommendedQuestion;
  toggleEssentialQuestionSelection: ToggleEssentialQuestionHandler;
  toggleQuestionSelection: ToggleQuestionHandler;
  toggleAnswerSelection: AnswerChangeHandler;
  toggleSelectionTypeView: ToggleSelectionTypeViewHandler;
  questionLimitReachedMessage?: React.ReactNode;
}

const getAnswersForDisplay = (
  question: StatefulRecommendedQuestion,
): AvailableAnswer[] => {
  const answerDict: { [key in SelectionTypeView]: AvailableAnswer[] } = {
    [SelectionTypeView.Answers]: question.availableAnswers,
    [SelectionTypeView.SimplifiedAnswers]: question.simplifiedAnswers,
  };

  return answerDict[question.selectionTypeView];
};

export const Question: React.FC<Props> = ({
  question,
  displayValidation = false,
  toggleQuestionSelection,
  questionLimitReachedMessage,
  toggleEssentialQuestionSelection,
  toggleSelectionTypeView,
  id,
  ...props
}) => {
  const { t } = useTranslations(translations);
  const isFilterAwarenessEnabled = useExperiment('Filter_Awareness');
  const getQuestionBorder = (isError: boolean, isSelected: boolean) => {
    if (isError) {
      return 'borderCritical';
    }
    if (isSelected) {
      return 'borderNeutralLight';
    }
    return undefined;
  };

  return (
    <Box
      id={id}
      boxShadow={getQuestionBorder(
        Boolean(
          (displayValidation && question.isError) ||
            questionLimitReachedMessage,
        ),
        question.selected,
      )}
      borderRadius={question.selected ? 'standard' : 'none'}
      paddingX="medium"
      paddingY={question.selected ? 'medium' : 'none'}
      onClick={() => {
        if (question.disabled) {
          toggleQuestionSelection(question.id);
        }
      }}
      data-testid="question-container"
    >
      <Stack space="medium">
        <Checkbox
          label={
            <>
              {question.text}
              {question.certsyVerifiable && (
                <>
                  {'\u00A0'}
                  <CertsyTooltip>
                    <IconSeekPass />
                  </CertsyTooltip>
                </>
              )}
              {isFilterAwarenessEnabled && question.isEssentialFilters && (
                <>
                  {'\u00A0\u00A0\u00A0'}
                  <Badge tone="promote">{t('question.text.filterBadge')}</Badge>
                </>
              )}
            </>
          }
          checked={question.selected}
          id={`q-${question.id}`}
          size="small"
          disabled={question.disabled}
          onChange={() => toggleQuestionSelection(question.id)}
          reserveMessageSpace={false}
          data={{}}
        >
          {question.selected && (
            <Stack space="medium">
              <SubHeader
                question={question}
                toggleSelectionTypeView={toggleSelectionTypeView}
              />
            </Stack>
          )}
        </Checkbox>
        {question.selected && (
          <>
            <AnswerList
              questionId={question.id}
              selectionType={question.selectionType}
              answers={getAnswersForDisplay(question)}
              {...props}
            />
            {displayValidation && question.isError && (
              <FieldMessage
                id={question.id}
                tone="critical"
                message={t('error.question.select-preferred-answers')}
              />
            )}
            <EssentialToggle
              {...question}
              onChange={toggleEssentialQuestionSelection}
            />
          </>
        )}
        {questionLimitReachedMessage ? (
          <Box>{questionLimitReachedMessage}</Box>
        ) : null}
      </Stack>
    </Box>
  );
};
